import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from 'shared/components/UC/Button';
import Toast from 'shared/components/Toast';
import { useIsAuthenticated } from '../contexts/AuthContext';
import evaluatorService from '../services/evaluatorService';
import {
  InvitationDetails,
  InvitationResponseResponse,
} from '../types/evaluator';
import styles from './EvaluatorInvitation.module.css';

type DeclineReason = 'thematic' | 'conflict' | 'other';

const DECLINE_REASON_LABELS: Record<DeclineReason, string> = {
  thematic: 'Área no afín',
  conflict: 'Conflicto de interés',
  other: 'Otro motivo',
};

const DECLINE_NOTE_HINTS: Record<DeclineReason, string> = {
  thematic:
    'Puede fundamentar en base al título y resumen ejecutivo del proyecto.',
  conflict:
    'Indique con qué integrante del equipo existe la relación y de qué tipo es.',
  other: 'Puede añadir detalles adicionales aquí.',
};

const ROLE_LABELS: Record<string, string> = {
  IR: 'Investigador Responsable',
  IP: 'Investigador Principal',
  IA: 'Investigador Asociado',
  COI: 'Co-Investigador',
};

const EvaluatorInvitation: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();

  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [decision, setDecision] = useState<'accept' | 'decline' | ''>('');
  const [declineReason, setDeclineReason] = useState<DeclineReason | ''>('');
  const [declineNote, setDeclineNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [response, setResponse] = useState<InvitationResponseResponse | null>(
    null
  );
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  useEffect(() => {
    const loadInvitation = async () => {
      if (!token) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      try {
        const details = await evaluatorService.getInvitation(token);
        setInvitation(details);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    loadInvitation();
  }, [token]);

  // The feedback screen replaces a long page — reset scroll so it reads
  // like a new view instead of landing mid-page.
  useEffect(() => {
    if (response) {
      window.scrollTo({ top: 0 });
    }
  }, [response]);

  const handleSubmit = async () => {
    if (!token || !decision) return;
    if (decision === 'decline' && !declineReason) return;
    setSubmitting(true);
    try {
      const result = await evaluatorService.respondToInvitation(token, {
        action: decision,
        reason: decision === 'decline' ? (declineReason as DeclineReason) : undefined,
        note: decision === 'decline' ? declineNote : undefined,
      });
      setResponse(result);
    } catch (error) {
      setToast({
        message:
          error instanceof Error
            ? error.message
            : 'Error al registrar la respuesta. Por favor intente nuevamente.',
        type: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const goToPanel = () => navigate('/evaluator/dashboard');
  // Lleva directo a la evaluación de esta postulación (no a la lista).
  const goToEvaluation = () => {
    if (!invitation) return;
    navigate(`/evaluator/evaluate/${invitation.applicationId}`);
  };
  const goToLogin = () => navigate('/login');
  const goToPasswordSetup = () => {
    if (!invitation) return;
    navigate(
      `/setup-password?token=${invitation.assignmentId}&email=${encodeURIComponent(invitation.evaluator.email)}`
    );
  };

  if (loading) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.card}>
          <p className="uc-p-body">Cargando invitación...</p>
        </div>
      </div>
    );
  }

  if (notFound || !invitation) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.card}>
          <h1 className="uc-h2">Enlace inválido</h1>
          <p className="uc-p-body">
            La invitación no existe o el enlace ha expirado. Si cree que se
            trata de un error, contacte a la Dirección de Investigación.
          </p>
        </div>
      </div>
    );
  }

  // Feedback screen after responding from this page.
  if (response) {
    const accepted = response.status === 'accepted_not_started';
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.card}>
          {accepted ? (
            <>
              <h1 className="uc-h2">Invitación aceptada</h1>
              <div className={styles.successBox}>
                <p className="uc-p-body">
                  Su aceptación y la declaración de ausencia de conflicto de
                  interés quedaron registradas para la postulación{' '}
                  <strong>{invitation.applicationNumber}</strong>.
                </p>
              </div>
              <p className="uc-p-body">
                Ya puede comenzar la evaluación. El proceso tiene 3 pasos:
                Instrucciones, Evaluación y Resumen, y puede guardar su avance
                en cualquier momento.
              </p>
              <div className={styles.feedbackActions}>
                {response.evaluatorConfirmed ? (
                  <Button variant="primary" onClick={goToEvaluation}>
                    Comenzar la evaluación
                  </Button>
                ) : (
                  <Button variant="primary" onClick={goToPasswordSetup}>
                    Configurar mis credenciales para comenzar
                  </Button>
                )}
              </div>
            </>
          ) : (
            <>
              <h1 className="uc-h2">Respuesta registrada</h1>
              <p className="uc-p-body">
                Gracias por informarnos. Su respuesta fue registrada y la
                Dirección de Investigación asignará otro evaluador para esta
                postulación.
                {isAuthenticated
                  ? ' Si lo desea, puede volver a su panel para gestionar sus otras evaluaciones.'
                  : invitation.evaluator.isConfirmed
                    ? ' Si lo desea, puede ingresar a su panel para gestionar sus otras evaluaciones.'
                    : ' Puede cerrar esta ventana.'}
              </p>
              {isAuthenticated ? (
                <div className={styles.feedbackActions}>
                  <Button variant="primary" onClick={goToPanel}>
                    Volver a mi panel de evaluación
                  </Button>
                </div>
              ) : (
                invitation.evaluator.isConfirmed && (
                  <div className={styles.feedbackActions}>
                    <Button variant="primary" onClick={goToLogin}>
                      Ir a mi panel de evaluación
                    </Button>
                  </div>
                )
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // Invitation already answered (or evaluation already underway/submitted).
  if (invitation.status !== 'pending') {
    const message: Record<string, { title: string; body: string; panelCta: boolean }> = {
      accepted_not_started: {
        title: 'Invitación ya aceptada',
        body: 'Usted ya aceptó esta invitación. Puede iniciar la evaluación desde su panel.',
        panelCta: true,
      },
      in_progress: {
        title: 'Evaluación en curso',
        body: 'Usted ya aceptó esta invitación y tiene la evaluación en curso.',
        panelCta: true,
      },
      completed: {
        title: 'Evaluación enviada',
        body: 'Usted ya envió la evaluación de esta postulación. Gracias por su colaboración.',
        panelCta: true,
      },
      declined: {
        title: 'Invitación declinada',
        body: 'Usted ya declinó esta invitación. Gracias por su respuesta.',
        panelCta: false,
      },
      conflict_declared: {
        title: 'Conflicto de interés declarado',
        body: 'Usted declaró un conflicto de interés para esta postulación, por lo que no puede evaluarla. Gracias por su transparencia.',
        panelCta: false,
      },
    };
    const state = message[invitation.status];
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.card}>
          <h1 className="uc-h2">{state.title}</h1>
          <p className="uc-p-body">{state.body}</p>
          {state.panelCta && (
            <div className={styles.feedbackActions}>
              <Button variant="primary" onClick={goToPanel}>
                Ir a mi panel de evaluación
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  const noteHint = declineReason ? DECLINE_NOTE_HINTS[declineReason] : '';

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.container}>
        <header className={styles.header}>
          <span className="uc-epigrafe-subtitulo">
            Avanza UC — Proceso de evaluación
          </span>
          <h1 className="uc-h1">
            Invitación a evaluar una postulación
          </h1>
          <p className="uc-p-body">
            Estimado/a <strong>{invitation.evaluator.name}</strong>: la
            Dirección de Investigación le invita a evaluar la siguiente
            postulación. Revise la información del proyecto y de su equipo
            antes de responder.
          </p>
        </header>

        <section className={styles.section}>
          <h2 className="uc-h3">Convocatoria</h2>
          <div className={styles.infoCard}>
            <div className={styles.infoRow}>
              <span className={`uc-p-body-small ${styles.label}`}>Folio:</span>
              <span className="uc-p-body">{invitation.applicationNumber}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={`uc-p-body-small ${styles.label}`}>
                Título del proyecto:
              </span>
              <span className="uc-p-body">{invitation.projectTitle}</span>
            </div>
            <div className={styles.summaryBlock}>
              <span className={`uc-p-body-small ${styles.label}`}>
                Resumen ejecutivo:
              </span>
              <div className={`uc-p-body ${styles.summaryText}`}>
                {invitation.executiveSummary}
              </div>
            </div>
          </div>
        </section>

        <section className={styles.section}>
          <h2 className="uc-h3">Integrantes del equipo</h2>
          <p className="uc-p-body-small">
            Revise el equipo completo antes de responder: un conflicto de
            interés puede existir con cualquiera de sus integrantes.
          </p>
          <table className={styles.teamTable}>
            <thead>
              <tr>
                <th className={styles.teamTh}>Rol</th>
                <th className={styles.teamTh}>Nombre</th>
                <th className={styles.teamTh}>Institución</th>
              </tr>
            </thead>
            <tbody>
              {invitation.team.map((m, i) => (
                <tr key={i} className={styles.teamTr}>
                  <td className={styles.teamTd}>
                    <span className={`${styles.roleBadge} ${styles[`role${m.role}`] ?? ''}`}>
                      {m.role}
                    </span>
                    <span className={`uc-p-body-small ${styles.roleLabel}`}>
                      {ROLE_LABELS[m.role] ?? m.role}
                    </span>
                  </td>
                  <td className={styles.teamTd}>{m.name}</td>
                  <td className={styles.teamTd}>
                    {m.affiliation}
                    {m.institutionType === 'Internacional' && m.country && (
                      <span className={styles.country}> ({m.country})</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className={styles.conflictInfo}>
            <h3 className={`uc-h4 ${styles.conflictHeading}`}>
              ¿Qué se considera conflicto de interés?
            </h3>
            <p className="uc-p-body">
              Cualquier relación personal, académica, profesional o financiera
              con algún integrante del equipo que pueda influir en su capacidad
              de realizar una evaluación objetiva e imparcial. Por ejemplo:
            </p>
            <ul className={`uc-p-body ${styles.conflictList}`}>
              <li>Relación familiar o personal cercana</li>
              <li>Colaboración académica o profesional reciente (últimos 3 años)</li>
              <li>Relación comercial o financiera</li>
              <li>Supervisión académica (tutor/estudiante)</li>
            </ul>
          </div>
        </section>

        <section className={styles.section}>
          <h2 className="uc-h3">Su respuesta</h2>
          <div className={styles.decisionGroup}>
            <label className={styles.decisionLabel}>
              <input
                type="radio"
                name="invitationDecision"
                value="accept"
                checked={decision === 'accept'}
                onChange={() => {
                  setDecision('accept');
                  setDeclineReason('');
                }}
                disabled={submitting}
              />
              <span className="uc-p-body">
                <strong>Acepto la invitación</strong> y declaro que no tengo
                conflicto de interés con ningún integrante del equipo.
              </span>
            </label>

            {/* Decline reasons are always visible: picking one IS declining
                (same radio group as accept — no extra click needed). */}
            <fieldset className={styles.declineBox}>
              <legend className={`uc-p-body ${styles.declineBoxTitle}`}>
                <strong>Declino la invitación con motivo de:</strong>
              </legend>
              <div className={styles.reasonList}>
                {(Object.keys(DECLINE_REASON_LABELS) as DeclineReason[]).map(
                  (r) => (
                    <label key={r} className={styles.reasonLabel}>
                      <input
                        type="radio"
                        name="invitationDecision"
                        value={`decline-${r}`}
                        checked={decision === 'decline' && declineReason === r}
                        onChange={() => {
                          setDecision('decline');
                          setDeclineReason(r);
                        }}
                        disabled={submitting}
                      />
                      <span className="uc-p-body">{DECLINE_REASON_LABELS[r]}</span>
                    </label>
                  )
                )}
              </div>
              {decision === 'decline' && declineReason && (
                <div className={styles.noteField}>
                  <label className={`uc-p-body-small ${styles.noteLabel}`}>
                    Especifique{' '}
                    <span className={styles.optional}>(opcional)</span>
                  </label>
                  <textarea
                    className={styles.noteTextarea}
                    rows={3}
                    placeholder={noteHint}
                    value={declineNote}
                    onChange={(e) => setDeclineNote(e.target.value)}
                    disabled={submitting}
                  />
                </div>
              )}
            </fieldset>
          </div>

          <div className={styles.submitRow}>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={
                submitting ||
                !decision ||
                (decision === 'decline' && !declineReason)
              }
            >
              {submitting ? 'Registrando...' : 'Confirmar respuesta'}
            </Button>
          </div>
        </section>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default EvaluatorInvitation;
